import {
  determineInitialScreen,
  determineScreenAfterPrimaryGoal,
} from 'src/lib/flows/utils';
import { Screen } from 'src/lib/flows/screens';
import { Input, Strengths } from 'src/lib/constants';
import { AllInputs, CreditScore } from 'src/types/inputs';
import { determineScreenAfterCreditScore } from 'src/lib/flows/utils';
import type { InputsReducer } from 'src/redux/reducers/inputs';
import type { QueryParams } from 'src/redux/reducers/query-params';
import {
  PrimaryGoal,
  SpecificAirlineOrHotelValues,
  RewardsAndPerks,
  CashBackPreference,
  IntroAPROrRewards,
} from 'src/types/inputs';
import type { ExperimentsReducer } from 'src/redux/reducers/experiments';

export interface Progress {
  show: boolean;
  value: number;
  label: string;
  max: number;
}
export type DataType = {
  inputs: InputsReducer;
  queryParams: QueryParams;
  isSignedIn: boolean;
  experiments: ExperimentsReducer;
  userEmail: string;
  isMobileApp?: boolean;
  auth0Variant?: string;
};

export interface FlowStrengths {
  start: Strengths;
  end: Strengths;
}

export interface FlowScreen {
  toScreen: Screen[] | ((data: DataType) => Screen);
  allowBack?: boolean;
  progress?:
    | Partial<Progress>
    | ((inputs: Partial<AllInputs>) => Partial<Progress>);
  progressRate?: number;
  inputs?: Input[];
  strength?: FlowStrengths;
  nerdyTip?: string;
}

export interface Flow {
  [screenName: string]: FlowScreen;
}

export const cashbackFlowFragment: Flow = {
  [Screen.CashBackPreference]: {
    toScreen({ inputs }) {
      return inputs[Input.CashBackPreference] === CashBackPreference.SameRate
        ? Screen.CashBackBigPurchase
        : Screen.CashBackSpendingCategories;
    },
    progress: {
      show: true,
      label: 'Rewards preference',
      value: 1,
      max: 2,
    },
    progressRate: 35,
    nerdyTip: `A flat rate cashback card can save you time at the register by offering bonus rates on all purchases. But if you don't mind a little thinking, you could earn more rewards on spending with bonus rates that complement one another.`,
    inputs: [Input.CashBackPreference],
  },
  [Screen.CashBackBigPurchase]: {
    toScreen: [Screen.MonthlySpend],
    progress: {
      show: true,
      label: 'Card features',
      value: 2,
      max: 2,
    },
    progressRate: 50,
    nerdyTip: `Some cards offer a 0% introductory APR period that can save you on interest if you need to rollover a balance on a big purchase. That means more time to pay off that new washing machine you love, interest-free.`,
    inputs: [Input.CashBackBigPurchase],
  },
  [Screen.CashBackSpendingCategories]: {
    toScreen: [Screen.ManagingCategories],
    progress: {
      show: true,
      label: 'Rewards preference',
      value: 1,
      max: 2,
    },
    progressRate: 50,
    nerdyTip: `Choosing a card with bonus categories that best match your spending can save you money on your credit card bill.`,
    inputs: [Input.SpendingCategories],
  },
  [Screen.ManagingCategories]: {
    toScreen: [Screen.MonthlySpend],
    progress: {
      show: true,
      label: 'Rewards preference',
      value: 1,
      max: 2,
    },
    progressRate: 63,
    nerdyTip: `Some cards offer flexibility with custom or rotating spending categories. You'll have to work a little harder to maximize their value, but the rewards rates are typically higher.`,
    inputs: [Input.ManagingCategories],
  },
  [Screen.MonthlySpend]: {
    toScreen({ isSignedIn, isMobileApp }) {
      if (!isSignedIn && !isMobileApp) {
        return Screen.Auth0;
      }
      return Screen.Loading;
    },
    progress: {
      show: true,
      label: 'Rewards and perks',
      value: 2,
      max: 2,
    },
    progressRate: 75,
    nerdyTip: `Many rewards cards have intro offers that net you bonus rewards for meeting spending thresholds when you open an account. Your typical spending helps identify which cards you'd likely earn the full intro offer on.`,
    inputs: [Input.MonthlySpend],
  },
};

export const bigPurchaseFlowFragment: Flow = {
  [Screen.HowLongToPayOffDebt]: {
    toScreen: [Screen.AnnualFees],
    progress: {
      show: true,
      label: 'Debt information',
      value: 1,
      max: 2,
    },
    progressRate: 43,
    nerdyTip: `The cards that have the longest 0% periods generally don’t offer the best rewards (if any). If you're okay with a little less time to pay off your purchase, there are cards with good rewards as well.`,
    inputs: [Input.HowLongToPayOffDebt],
  },
  [Screen.AnnualFees]: {
    toScreen({ isSignedIn, isMobileApp }) {
      if (!isSignedIn && !isMobileApp) {
        return Screen.Auth0;
      }
      return Screen.Loading;
    },
    progress: {
      show: true,
    },
    progressRate: 67,
    nerdyTip: `Annual fees eat into the savings you may get from a card with a 0% interest period, but sometimes the rewards are worthwhile.`,
    inputs: [Input.AnnualFees],
  },
};

export const debtFlowVariantFragment: Flow = {
  [Screen.AddDebtScreen]: {
    toScreen: [Screen.IntroAPROrRewards],
    progress: {
      show: true,
      label: 'Debt information',
      value: 1,
      max: 3,
    },
    progressRate: 43,
    nerdyTip: `Balance transfers can save you money on high interest debt. You'll incur a 3-5% fee upfront but have a long time to pay it off at 0% interest. Most issuers won’t let you transfer debt from one of their cards to another one of their cards, so be mindful where you apply.`,
    inputs: [Input.FullDebt],
  },
  [Screen.IntroAPROrRewards]: {
    toScreen({ inputs, isSignedIn, isMobileApp }) {
      if (inputs[Input.PrimaryGoal] !== PrimaryGoal.Debt) {
        return Screen.PurchaseSpend;
      }

      if (inputs[Input.IntroAPROrRewards] === IntroAPROrRewards.IntroAPR) {
        if (!isSignedIn && !isMobileApp) {
          return Screen.Auth0;
        }
        return Screen.Loading;
      } else return Screen.MonthlySpend;
    },
    progress: {
      show: true,
      label: 'Card features',
      value: 2,
      max: 3,
    },
    progressRate: 67,
    nerdyTip: `The cards that have the longest 0% periods generally don’t offer the best rewards (if any). If you're okay with a little less time to pay off your debt, there are cards with good rewards as well.`,
    inputs: [Input.IntroAPROrRewards],
  },
  [Screen.MonthlySpend]: {
    toScreen({ isSignedIn, isMobileApp }) {
      if (!isSignedIn && !isMobileApp) {
        return Screen.Auth0;
      }
      return Screen.Loading;
    },
    progress: {
      show: true,
      label: 'Rewards and perks',
      value: 3,
      max: 3,
    },
    progressRate: 79,
    nerdyTip: `Many credit cards have intro offers that net you bonus rewards for meeting spending thresholds when you open an account. Your typical spending helps identify which cards you'd likely earn the full intro offer on.`,
    inputs: [Input.MonthlySpend],
  },
};

export const debtFlowFragment: Flow = {
  [Screen.IsDebtCreditCard]: {
    toScreen({ inputs }) {
      return inputs[Input.IsDebtCreditCard]
        ? Screen.CurrentBalanceCompany
        : Screen.TypeOfDebt;
    },
    progress: {
      show: true,
      label: 'Debt information',
      value: 1,
      max: 2,
    },
    progressRate: 35,
    nerdyTip: `A balance transfer to a credit card can save you money on high interest debt. You'll incur a 3-5% fee upfront but have a long time period to pay it off at 0% interest.`,
    inputs: [Input.IsDebtCreditCard],
  },
  [Screen.TypeOfDebt]: {
    toScreen: [Screen.CurrentBalanceCompany],
    progress: {
      show: true,
      label: 'Debt information',
      value: 1,
      max: 2,
    },
    progressRate: 43,
    nerdyTip: `Balance transfers are geared primarily towards paying down your credit card debt, but some issuers allow you to transfer other types of debt like personal loans.`,
    inputs: [Input.TypeOfDebt],
  },
  [Screen.CurrentBalanceCompany]: {
    toScreen: [Screen.IntroAPROrRewards],
    progress: {
      show: true,
      label: 'Debt information',
      value: 1,
      max: 2,
    },
    progressRate: 50,
    nerdyTip: `Most issuers won’t let you transfer debt from one of their cards to another, so make sure you aren’t applying for a card from the same bank that holds the debt you want to transfer.`,
    inputs: [Input.CurrentBalanceCompany],
  },
  [Screen.IntroAPROrRewards]: {
    toScreen({ inputs, isSignedIn, isMobileApp }) {
      if (inputs[Input.PrimaryGoal] !== PrimaryGoal.Debt) {
        return Screen.PurchaseSpend;
      }

      if (inputs[Input.IntroAPROrRewards] === IntroAPROrRewards.IntroAPR) {
        if (!isSignedIn && !isMobileApp) {
          return Screen.Auth0;
        }
        return Screen.Loading;
      } else return Screen.MonthlySpend;
    },
    progress: {
      show: true,
      label: 'Card features',
      value: 2,
      max: 2,
    },
    progressRate: 75,
    nerdyTip: `The cards that have the longest 0% periods generally don’t offer the best rewards (if any). If you're okay with a little less time to pay off your debt, there are cards with good rewards as well.`,
    inputs: [Input.IntroAPROrRewards],
  },
  [Screen.MonthlySpend]: {
    toScreen({ isSignedIn, isMobileApp }) {
      if (!isSignedIn && !isMobileApp) {
        return Screen.Auth0;
      }
      return Screen.Loading;
    },
    progress: {
      show: true,
      label: 'Rewards and perks',
      value: 2,
      max: 2,
    },
    progressRate: 85,
    nerdyTip: `Many credit cards have intro offers that net you bonus rewards for meeting spending thresholds when you open an account. Your typical spending helps identify which cards you'd likely earn the full intro offer on.`,
    inputs: [Input.MonthlySpend],
  },
};

export const buildCreditVariantFlowFragment: Flow = {
  [Screen.CollegeStudent]: {
    toScreen({ inputs }) {
      if (inputs[Input.CollegeStudent]) {
        return Screen.StudentSpendingCategories;
      }
      return Screen.SecurityDeposit;
    },
    progress: {
      show: true,
      label: 'Student status',
      value: 1,
      max: 3,
    },
    progressRate: 37,
    strength: {
      start: Strengths.Intermediate,
      end: Strengths.Strong,
    },
    nerdyTip: `Some issuers offer cards specifically designed for college students to build their credit and learn how to manage debt. You may need to prove you're registered at a school.`,
    inputs: [Input.CollegeStudent],
  },
  [Screen.SecurityDeposit]: {
    toScreen: [Screen.AnnualFees],
    progress: (inputs) => ({
      show: true,
      label: 'Security deposit',
      value:
        inputs[Input.CreditScore] === CreditScore.LimitedCreditHistory ? 2 : 1,
      max:
        inputs[Input.CreditScore] === CreditScore.LimitedCreditHistory ? 3 : 2,
    }),
    progressRate: 54,
    nerdyTip: `Most cards for building credit require a minimum deposit which functions as your credit limit. With responsible usage, this deposit will be returned when you upgrade to a better card (or close the account).`,
    inputs: [Input.SecurityDeposit],
  },
  [Screen.StudentSpendingCategories]: {
    toScreen: [Screen.MonthlySpend],
    progress: {
      show: true,
      label: 'Spending preference',
      value: 2,
      max: 3,
    },
    progressRate: 54,
    strength: {
      start: Strengths.Intermediate,
      end: Strengths.Strong,
    },

    nerdyTip: `Choosing a card with bonus categories that best match your spending can save you money on your credit card bill.`,
    inputs: [Input.StudentSpendingCategories],
  },
  [Screen.MonthlySpend]: {
    toScreen({ isSignedIn, isMobileApp }) {
      if (!isSignedIn && !isMobileApp) {
        return Screen.Auth0;
      }
      return Screen.Loading;
    },
    progress: {
      show: true,
      label: 'Rewards and perks',
      value: 3,
      max: 3,
    },
    progressRate: 71,
    nerdyTip: `Some student cards have intro offers that net you bonus rewards for meeting spending thresholds when you open an account. Your typical spending helps identify which cards you'd likely earn the full intro offer on.`,
    inputs: [Input.MonthlySpend],
  },
  [Screen.AnnualFees]: {
    toScreen({ isSignedIn, isMobileApp }) {
      if (!isSignedIn && !isMobileApp) {
        return Screen.Auth0;
      }
      return Screen.Loading;
    },
    progress: (inputs) => ({
      show: true,
      label: 'Annual fees',
      value:
        inputs[Input.CreditScore] === CreditScore.LimitedCreditHistory ? 3 : 2,
      max:
        inputs[Input.CreditScore] === CreditScore.LimitedCreditHistory ? 3 : 2,
    }),
    progressRate: 71,
    nerdyTip: `Cards for building credit sometimes come with annual fees, which help protect issuers from the risk of approving someone with bad credit. A revolving credit balance is beneficial for building credit so the cost may be worthwhile for you.`,
    inputs: [Input.AnnualFees],
  },
};

export const buildCreditFlowFragment: Flow = {
  [Screen.CreditHistory]: {
    toScreen({ inputs }) {
      if (inputs[Input.IsBuildingCredit]) {
        return Screen.CollegeStudent;
      }
      return Screen.CardDeposit;
    },
    progress: {
      show: true,
      label: 'Credit history',
      value: 1,
      max: 3,
    },
    progressRate: 37,
    strength: {
      start: Strengths.AveragePlus,
      end: Strengths.Intermediate,
    },
    nerdyTip: `The length of your credit history may influence which cards are best fit for your situation.`,
    inputs: [Input.IsBuildingCredit],
  },
  [Screen.CollegeStudent]: {
    toScreen: [Screen.AnnualFees],
    progress: {
      show: true,
      label: 'Student status',
      value: 2,
      max: 3,
    },
    progressRate: 54,
    strength: {
      start: Strengths.Intermediate,
      end: Strengths.Strong,
    },
    nerdyTip: `Some issuers offer cards specifically designed for college students to build their credit and learn how to manage debt. You may need to prove you're registered at a school.`,
    inputs: [Input.CollegeStudent],
  },
  [Screen.CardDeposit]: {
    toScreen: [Screen.AnnualFees],
    progress: {
      show: true,
      label: 'Card deposit',
      value: 2,
      max: 3,
    },
    progressRate: 54,
    strength: {
      start: Strengths.Intermediate,
      end: Strengths.Strong,
    },
    nerdyTip: `Most cards for building credit require a minimum deposit which functions as your credit limit. With responsible usage, this deposit will be returned when you upgrade to a better card (or close the account).`,
    inputs: [Input.CardDeposit],
  },
  [Screen.AnnualFees]: {
    toScreen({ isSignedIn, isMobileApp }) {
      if (!isSignedIn && !isMobileApp) {
        return Screen.Auth0;
      }
      return Screen.Loading;
    },
    progress: {
      show: true,
    },
    progressRate: 71,
    nerdyTip: `Cards for building credit sometimes come with annual fees, which help protect issuers from the risk of approving someone with bad credit. A revolving credit balance is beneficial for building credit so the cost may be worthwhile for you.`,
    inputs: [Input.AnnualFees],
  },
};

export const travelFlowFragment: Flow = {
  [Screen.InternationalTravel]: {
    toScreen: [Screen.SpecificAirlineOrHotel],
    progress: {
      show: true,
      label: 'Travel preferences',
      value: 1,
      max: 2,
    },
    progressRate: 34,
    nerdyTip: `International purchases often incur a foreign transaction fee of 1-3%. Some cards will waive this cost and save you money on your international travel.`,
    inputs: [Input.InternationalTravel],
  },
  [Screen.SpecificAirlineOrHotel]: {
    toScreen({ inputs }) {
      switch (inputs[Input.SpecificAirlineOrHotel]) {
        case SpecificAirlineOrHotelValues.YesAirline:
        case SpecificAirlineOrHotelValues.YesBoth:
          return Screen.AirlineChoice;
        case SpecificAirlineOrHotelValues.YesHotel:
          return Screen.HotelChoice;
        default:
          return Screen.RewardsAndPerks;
      }
    },
    progress: {
      show: true,
      label: 'Travel preferences',
      value: 1,
      max: 2,
    },
    progressRate: 48,
    nerdyTip: `A cobranded airline or hotel card can offer outsized travel savings if you're loyal to a specific brand. Careful though, reward redemption is not as flexible as with a general-purpose travel card.`,
    inputs: [Input.SpecificAirlineOrHotel],
  },
  [Screen.AirlineChoice]: {
    toScreen({ inputs }) {
      return inputs[Input.SpecificAirlineOrHotel] ===
        SpecificAirlineOrHotelValues.YesBoth
        ? Screen.HotelChoice
        : Screen.RewardsAndPerks;
    },
    progress: {
      show: true,
      label: 'Travel preferences',
      value: 1,
      max: 2,
    },
    progressRate: 53,
    nerdyTip: `Airline-branded cards earn points on purchases, usually called "miles", which can be used to book free flights. They also often come with perks like free checked bags and access to airport lounges.`,
    inputs: [Input.AirlineChoice],
  },
  [Screen.HotelChoice]: {
    toScreen: [Screen.RewardsAndPerks],
    progress: {
      show: true,
      label: 'Travel preference',
      value: 1,
      max: 2,
    },
    progressRate: 58,
    nerdyTip: `Hotel-branded cards often entitle you to great perks and bonus rewards at their hotels. They typically come with automatic elite status and some even offer free night stays annually.`,
    inputs: [Input.HotelChoice],
  },
  [Screen.RewardsAndPerks]: {
    toScreen({ inputs }) {
      return inputs[Input.RewardsAndPerks] ===
        RewardsAndPerks.HasLuxuryPerksTravelPleasant
        ? Screen.LuxuryPerks
        : Screen.SpendingCategories;
    },
    progress: {
      show: true,
      label: 'Rewards and perks',
      value: 2,
      max: 2,
    },
    progressRate: 62,
    nerdyTip: `Some cards offer a mix of rewards and perks, but most lean one way or the other. Cards with strong rewards will save you money on travel, but may not offer perks like airport lounge access that make travel more comfortable.`,
    inputs: [Input.RewardsAndPerks],
  },
  [Screen.SpendingCategories]: {
    toScreen({ inputs }) {
      return inputs[Input.RewardsAndPerks] ===
        RewardsAndPerks.HasMixRewardsAndTravelPerks
        ? Screen.LuxuryPerks
        : Screen.MonthlySpend;
    },
    progress: {
      show: true,
      label: 'Rewards and perks',
      value: 2,
      max: 2,
    },
    progressRate: 67,
    nerdyTip: `Choosing a card with bonus categories that best match your spending will help maximize rewards and save you money on future travel.`,
    inputs: [Input.SpendingCategories],
  },
  [Screen.LuxuryPerks]: {
    toScreen: [Screen.MonthlySpend],
    progress: {
      show: true,
      label: 'Rewards and perks',
      value: 2,
      max: 2,
    },
    progressRate: 72,
    nerdyTip: `Cards with travel perks can make travel more pleasant, but often come with high annual fees. The total value of the perks will usually outweigh the annual fee, but make sure you choose a card with perks you plan to use.`,
    inputs: [Input.LuxuryPerks],
  },
  [Screen.MonthlySpend]: {
    toScreen({ isSignedIn, isMobileApp }) {
      if (!isSignedIn && !isMobileApp) {
        return Screen.Auth0;
      }
      return Screen.Loading;
    },
    progress: {
      show: true,
      label: 'Rewards and perks',
      value: 2,
      max: 2,
    },
    progressRate: 76,
    nerdyTip: `Many rewards cards have intro offers that net you bonus rewards for meeting spending thresholds when you open an account. Your typical spending helps identify which cards you'd likely earn the full intro offer on.`,
    inputs: [Input.MonthlySpend],
  },
};

export const variantBigPurchaseFlowFragment: Flow = {
  [Screen.IntroAPROrRewards]: {
    toScreen: [Screen.PurchaseSpendingCategories],
    progressRate: 37,
    inputs: [Input.IntroAPROrRewards],
  },
  [Screen.PurchaseSpendingCategories]: {
    toScreen: [Screen.PurchaseSpendInput],
    progressRate: 55,
    inputs: [Input.PurchaseSpendingCategories],
  },
  [Screen.PurchaseSpendInput]: {
    toScreen({ isSignedIn, isMobileApp }) {
      if (!isSignedIn && !isMobileApp) {
        return Screen.Auth0;
      }
      return Screen.Loading;
    },
    progressRate: 72,
    inputs: [Input.PurchaseSpend],
  },
};

export const flow: Flow = {
  [Screen.WelcomeBack]: {
    toScreen({ queryParams, experiments }) {
      return determineInitialScreen({
        queryParams,
        isReturnUser: false,
        experiments,
      });
    },
  },
  [Screen.CreditScore]: {
    toScreen({ inputs, queryParams, experiments }) {
      return determineScreenAfterCreditScore({
        inputs,
        queryParams,
        experiments,
      });
    },
    strength: {
      start: Strengths.Weak,
      end: Strengths.Average,
    },
    progressRate: 10,
    nerdyTip:
      'A rough idea of your credit score helps us determine which types of credit card might be a good fit for you.',
    inputs: [Input.CreditScore],
  },
  [Screen.PrimaryGoal]: {
    toScreen({ inputs, experiments }) {
      return determineScreenAfterPrimaryGoal({
        primaryGoal: inputs[Input.PrimaryGoal] as PrimaryGoal,
        experiments,
      });
    },
    progressRate: 20,
    strength: {
      start: Strengths.Average,
      end: Strengths.AveragePlus,
    },
    nerdyTip: `Credit cards have different features that can help you earn rewards, save money on interest, and more. Your goal helps us narrow in on what's most important to you.`,
    inputs: [Input.PrimaryGoal],
  },
  [Screen.Auth0]: {
    toScreen: [Screen.Loading],
    progressRate: 90,
    inputs: [],
  },
};
